import { Accordion, Card, Code, Grid, ScrollArea, Text } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { Event } from '@/types/event'
import { EventTagList } from '../EventTagList/EventTagList'
import { extractObjectName } from '../helpers/extractDetections'
import { DetectionInfo } from './DetectionInfo/DetectionInfo'
import { EventInfoCard } from './EventInfoCard/EventInfoCard'
import { TrackedPeopleHandler } from './EventTrackedPeople/TrackedPeopleHandler'
import { ExtractionResults } from './ExtractionResults/ExtractionResults'
import { MeasurementInfo } from './MeasurementInfo/MeasurementInfo'
import { ObjectList } from './ObjectList/ObjectList'

type EventDetailRightPaneProps = {
  event: Event
}

export const EventDetailRightPane = ({ event }: EventDetailRightPaneProps) => {
  const intl = useIntl()

  const outputStream = event.output.stream

  const tags = 'tags' in event ? (event.tags ?? []) : []
  const classifierTags = tags.filter((t) => t.tag_source === 'classifier')
  const aiTags = tags.filter(
    (t) => t.tag_source === 'vp_agent' || t.tag_source === 'vp_agent_alert'
  )
  const description = 'description' in event ? event.description : undefined

  const ocr_results = 'ocr_results' in event ? (event.ocr_results ?? []) : [] // support legacy events

  const machine_code_results =
    'machine_code_results' in event ? (event.machine_code_results ?? []) : []

  const tracked_people =
    'tracked_people' in event ? (event.tracked_people ?? []) : []

  const objects = 'objects' in event ? (event.objects ?? []) : []

  return (
    <Grid.Col span={{ base: 12, md: 5, lg: 5 }}>
      <ScrollArea h="80vh">
        <EventInfoCard title={intl.formatMessage({ id: 'events.detections' })}>
          <Card.Section>
            <DetectionInfo
              detectionName={extractObjectName(event.detections)}
              confidence={'confidence' in event ? event.confidence : undefined}
              dwellTime={'dwell_time' in event ? event.dwell_time : undefined}
            />
          </Card.Section>
        </EventInfoCard>

        {description && (
          <EventInfoCard
            title={intl.formatMessage({ id: 'events.sceneDescription' })}
          >
            <Card.Section py="sm" inheritPadding>
              <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
                {description}
              </Text>
            </Card.Section>
          </EventInfoCard>
        )}

        {classifierTags.length > 0 && (
          <EventInfoCard
            title={intl.formatMessage({ id: 'events.tags.classification' })}
          >
            <Card.Section py="sm" inheritPadding>
              <EventTagList tags={classifierTags} />
            </Card.Section>
          </EventInfoCard>
        )}

        {aiTags.length > 0 && (
          <EventInfoCard
            title={intl.formatMessage({ id: 'events.tags.other' })}
          >
            <Card.Section py="sm" inheritPadding>
              <EventTagList tags={aiTags} />
            </Card.Section>
          </EventInfoCard>
        )}

        {objects.length > 0 && (
          <EventInfoCard title={intl.formatMessage({ id: 'events.objects' })}>
            <Card.Section py="sm" inheritPadding>
              <ObjectList objects={objects} />
            </Card.Section>
          </EventInfoCard>
        )}

        {ocr_results.length > 0 && (
          <EventInfoCard
            title={intl.formatMessage({ id: 'events.ocr.results' })}
          >
            <Card.Section>
              <ExtractionResults
                results={ocr_results.map((ocr_result) => ({
                  extractionString: ocr_result.ocr_string,
                  description: intl.formatMessage(
                    {
                      id: 'events.ocr.confidence'
                    },
                    {
                      confidence: ocr_result.ocr_confidence
                    }
                  ),
                  cropPath: ocr_result.ocr_crop_path,
                  status: ocr_result.ocr_tag
                }))}
              />
            </Card.Section>
          </EventInfoCard>
        )}

        {machine_code_results.length > 0 && (
          <EventInfoCard
            title={intl.formatMessage({ id: 'events.qr_barcode.results' })}
          >
            <Card.Section>
              <ExtractionResults
                results={machine_code_results.map((machine_code_result) => ({
                  extractionString: machine_code_result.machine_code_string,
                  description: machine_code_result.machine_code_type_name,
                  cropPath: machine_code_result.machine_code_crop_path
                }))}
              />
            </Card.Section>
          </EventInfoCard>
        )}

        {tracked_people.length > 0 && (
          <EventInfoCard
            title={intl.formatMessage({ id: 'events.trackedPeople' })}
          >
            <Card.Section py="sm" inheritPadding>
              <TrackedPeopleHandler
                appId={event.metadata.app_id}
                people={tracked_people}
              />
            </Card.Section>
          </EventInfoCard>
        )}

        {event.measurement && (
          <EventInfoCard
            title={intl.formatMessage({ id: 'events.measurement' })}
          >
            <Card.Section>
              <MeasurementInfo
                id={event.measurement.id}
                width={event.measurement.dimensions.width}
                height={event.measurement.dimensions.height}
                depth={event.measurement.dimensions.depth}
                sizeUnit={event.measurement.dimensions.unit}
                weight={event.measurement.weight.value}
                weightUnit={event.measurement.weight.unit}
              />
            </Card.Section>
          </EventInfoCard>
        )}

        <EventInfoCard
          title={intl.formatMessage({ id: 'events.configuration' })}
        >
          <Card.Section>
            <Accordion defaultValue="parameters">
              <Accordion.Item value="parameters">
                <Accordion.Control>
                  <Text size="sm" fw={600}>
                    <FormattedMessage id="events.configuration.parameters" />
                  </Text>
                </Accordion.Control>

                <Accordion.Panel>
                  <ScrollArea h={400}>
                    <Code bg="gray.2" block>
                      {JSON.stringify(outputStream.business_logics, null, 2)}
                    </Code>
                  </ScrollArea>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </Card.Section>
        </EventInfoCard>
      </ScrollArea>
    </Grid.Col>
  )
}
